import { useMemo, useState } from 'react'
import type {
  BorderEdge,
  CenteringLineSettings,
  CenteringSide,
  ClosestMatchSummary,
  GradeShareData,
  GradeSideAnalysis,
  GradingProvider,
  MostSimilarGrade,
} from '../lib/types'
import { GRADING_PROVIDERS } from '../lib/types'
import { formatGrade } from '../lib/format'
import { ProviderLogo } from './icons'
import { AnalysisOverlayCanvas } from './slab/AnalysisOverlayCanvas'
import styles from './report.module.css'

type Side = 'front' | 'back'

const SIDE_OPTIONS: Side[] = ['front', 'back']

export function ClosestMatchCard({ data }: { data: GradeShareData }) {
  const match = data.most_similar_grade
  const summary = data.closest_match_summary
  const [side, setSide] = useState<Side>('front')

  const rows = useMemo(() => getClosestMatchBreakdownRows(summary), [summary])
  if (!match) return null

  const provider = normalizeProvider(match.provider || summary?.provider || 'PSA')
  const providerLabel = (match.provider || summary?.provider || 'PSA').toUpperCase()
  const certNumber = match.cert_number || summary?.cert_number
  const matchTitle = match.title?.trim() || match.subject?.trim() || 'PSA match'
  const psaUrl = match.psa_url || summary?.psa_url || (certNumber ? `https://www.psacard.com/cert/${certNumber}` : '')
  const matchGrade = matchGradeLabel(match, provider, summary)
  const matchPercentNumber = getPercentNumber(
    summary?.match_percent ?? summary?.match_percentages?.overall ?? summary?.score,
  )
  const matchPercent = getPercentDisplay(matchPercentNumber)
  const totalClass =
    matchPercentNumber !== null && matchPercentNumber > 75
      ? styles.mostSimilarTotalPositive
      : styles.mostSimilarTotalWarning
  const avatarUrl = firstNonEmpty(match.official_front_url, match.display_front_processed_file_url, match.front_file_url)

  const headerContent = (
    <>
      {avatarUrl ? <img className={styles.mostSimilarAvatar} src={avatarUrl} alt="" /> : <span className={styles.mostSimilarAvatar} />}
      <span className={styles.mostSimilarHeaderText}>
        <span className={styles.mostSimilarSlabTitleRow}>
          <span className={styles.mostSimilarSlabTitleText}>{matchTitle}</span>
          {psaUrl ? <span className={styles.mostSimilarExternalArrow}>↗</span> : null}
        </span>
        <span className={styles.mostSimilarMetaLine}>
          {provider ? <ProviderLogo provider={provider} height={14} /> : <span>{providerLabel}</span>}
          {matchGrade ? <span>{matchGrade}</span> : null}
          {certNumber ? <span>#{certNumber}</span> : null}
        </span>
      </span>
    </>
  )

  return (
    <section className={styles.mostSimilarSection} aria-label="Closest condition match">
      <div className={styles.mostSimilarTitleGroup}>
        <h2 className={styles.analysisSectionTitle}>Closest condition match</h2>
        <p className={styles.mostSimilarBody}>
          We found a real PSA slab that has matching damage and centering to your card.
        </p>
      </div>

      {psaUrl ? (
        <a className={styles.mostSimilarHeader} href={psaUrl} rel="noreferrer" target="_blank">
          {headerContent}
        </a>
      ) : (
        <div className={styles.mostSimilarHeader}>{headerContent}</div>
      )}

      <div className={styles.mostSimilarBreakdown}>
        <div className={styles.mostSimilarTotalBlock}>
          <div className={styles.mostSimilarTotalHeader}>
            <h3>Condition match</h3>
            <span className={`${styles.mostSimilarTotalValue} ${totalClass}`}>
              {matchPercent ?? '-'}
            </span>
          </div>
          <div className={styles.mostSimilarTotalTrack}>
            <span
              className={`${styles.mostSimilarTotalFill} ${totalClass}`}
              style={{ width: `${Math.max(0, Math.min(100, matchPercentNumber ?? 0))}%` }}
            />
          </div>
        </div>
        <div className={styles.mostSimilarBreakdownInset}>
          {rows.map((row) => {
            const percent = getPercentDisplay(row.value)
            const closeness = getSimilarityClosenessLabel(row.value)
            return (
              <div key={row.key} className={styles.mostSimilarBreakdownRow}>
                <span className={styles.mostSimilarBreakdownLabel}>{row.label}</span>
                <span className={styles.mostSimilarBreakdownValueGroup}>
                  {closeness ? <span>{closeness}</span> : null}
                  <strong>{percent ?? '-'}</strong>
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <div className={styles.mostSimilarSideBySide}>
        <div className={styles.mostSimilarSideBySideHeader}>
          <h3>Side by side</h3>
          <p>Raw single, compared to your card</p>
        </div>
        <div className={styles.mostSimilarCanvasRow}>
          <ImageColumn
            label={certNumber ? `${providerLabel} ${matchGrade ? `${matchGrade} ` : ''}#${certNumber}` : 'PSA match'}
            imageUrl={matchImageUrl(match, side)}
            analysis={matchSideAnalysis(match, side)}
            centering={matchSideCentering(match, side)}
          />
          <ImageColumn
            label="Your card"
            imageUrl={side === 'front' ? data.card.frontImageUrl : data.card.backImageUrl}
            analysis={data.analysis?.[side]}
            centering={data.centering?.[side]}
            rectified={
              side === 'front'
                ? data.card.frontImageRectificationApplied
                : data.card.backImageRectificationApplied
            }
          />
        </div>
        <div className={styles.mostSimilarSwitcher}>
          {SIDE_OPTIONS.map((option) => {
            const active = side === option
            return (
              <button
                className={`${styles.mostSimilarSwitchButton} ${active ? styles.mostSimilarSwitchButtonActive : ''}`}
                key={option}
                onClick={() => setSide(option)}
                type="button"
              >
                {option === 'front' ? 'Front' : 'Back'}
              </button>
            )
          })}
        </div>
      </div>
    </section>
  )
}

function ImageColumn({
  label,
  imageUrl,
  analysis,
  centering,
  rectified = false,
}: {
  label: string
  imageUrl?: string
  analysis?: GradeSideAnalysis
  centering?: CenteringSide
  rectified?: boolean
}) {
  return (
    <div className={styles.mostSimilarCanvasColumn}>
      <div className={styles.similarGradeCanvasFrame}>
        {imageUrl ? (
          <>
            <img src={imageUrl} alt="" />
            <AnalysisOverlayCanvas
              analysis={analysis}
              centering={centering}
              imageUrl={imageUrl}
              rectified={rectified}
            />
          </>
        ) : (
          <span className={styles.similarGradeEmptyCanvas} />
        )}
      </div>
      <span className={styles.mostSimilarCanvasLabel}>{label}</span>
    </div>
  )
}

function normalizeProvider(provider: string): GradingProvider | null {
  const value = provider.toUpperCase()
  return GRADING_PROVIDERS.includes(value as GradingProvider) ? (value as GradingProvider) : null
}

function matchGradeLabel(
  match: MostSimilarGrade,
  provider: GradingProvider | null,
  summary?: ClosestMatchSummary | null,
) {
  if (typeof match.actual_grade === 'number' && Number.isFinite(match.actual_grade)) {
    return provider ? formatGrade(match.actual_grade, provider) : String(match.actual_grade)
  }
  if (summary?.grade != null) return String(summary.grade)
  return match.grade_label ?? ''
}

function matchImageUrl(match: MostSimilarGrade, side: Side) {
  return side === 'front'
    ? firstNonEmpty(
        match.display_front_processed_file_url,
        match.front_processed_file_url,
        match.front_file_url,
        match.front_thumbnail_file_url,
        match.display_front_overlay_file_url,
        match.front_overlay_file_url,
        match.official_front_url,
      )
    : firstNonEmpty(
        match.display_back_processed_file_url,
        match.back_processed_file_url,
        match.back_file_url,
        match.back_thumbnail_file_url,
        match.display_back_overlay_file_url,
        match.back_overlay_file_url,
        match.official_back_url,
      )
}

function matchSideCentering(match: MostSimilarGrade, side: Side): CenteringSide | undefined {
  const nested = match.centering?.[side]
  if (nested) return nested
  const top = side === 'front' ? match.front_top_grade : match.back_top_grade
  const bottom = side === 'front' ? match.front_bottom_grade : match.back_bottom_grade
  const left = side === 'front' ? match.front_left_grade : match.back_left_grade
  const right = side === 'front' ? match.front_right_grade : match.back_right_grade
  if (![top, bottom, left, right].some(isFiniteNumber)) return undefined
  return {
    top: top ?? 0.5,
    bottom: bottom ?? 0.5,
    left: left ?? 0.5,
    right: right ?? 0.5,
  }
}

function matchSideAnalysis(match: MostSimilarGrade, side: Side): GradeSideAnalysis | undefined {
  const nested = match.analysis?.[side]
  const flat = flatMatchSideAnalysis(match, side)
  if (!nested) return flat
  if (!flat) return nested
  return {
    ...nested,
    sourceWidth: nested.sourceWidth || flat.sourceWidth,
    sourceHeight: nested.sourceHeight || flat.sourceHeight,
    damageMaskPaths: nested.damageMaskPaths?.length ? nested.damageMaskPaths : flat.damageMaskPaths,
    damageDetections: nested.damageDetections?.length ? nested.damageDetections : flat.damageDetections,
    borderDetections: nested.borderDetections?.length ? nested.borderDetections : flat.borderDetections,
    borderCoords: hasBorderCoords(nested.borderCoords) ? nested.borderCoords : flat.borderCoords,
    borderWidthRatios: nested.borderWidthRatios ?? flat.borderWidthRatios,
    borderErrorSides: nested.borderErrorSides?.length ? nested.borderErrorSides : flat.borderErrorSides,
    centeringLines: hasCenteringLines(nested.centeringLines) ? nested.centeringLines : flat.centeringLines,
  }
}

function flatMatchSideAnalysis(match: MostSimilarGrade, side: Side): GradeSideAnalysis | undefined {
  const front = side === 'front'
  const damageMaskPaths = front ? match.front_mask_paths : match.back_mask_paths
  const damageDetections = sideDetections(match.damage_detections, side)
  const borderDetections = sideDetections(match.border_detections, side)
  const borderCoords = cleanBorderCoords(front ? match.front_border_coords : match.back_border_coords)
  const borderErrorSides = front ? match.front_border_error_sides : match.back_border_error_sides
  const centeringLines = flatCenteringLines(match, side)
  const hasArtifacts = Boolean(
    damageMaskPaths?.length ||
      damageDetections?.length ||
      borderDetections?.length ||
      hasBorderCoords(borderCoords) ||
      borderErrorSides?.length ||
      hasCenteringLines(centeringLines),
  )
  if (!hasArtifacts) return undefined
  return {
    sourceWidth: (front ? match.front_mask_source_width : match.back_mask_source_width) ?? 0,
    sourceHeight: (front ? match.front_mask_source_height : match.back_mask_source_height) ?? 0,
    damageMaskPaths,
    damageDetections,
    borderDetections,
    borderCoords,
    borderWidthRatios: {
      h: (front ? match.front_border_width_ratio_h : match.back_border_width_ratio_h) || 0.12,
      v: (front ? match.front_border_width_ratio_v : match.back_border_width_ratio_v) || 0.12,
    },
    borderErrorSides,
    centeringLines,
  }
}

function sideDetections<T extends { side?: string }>(detections: T[] | null | undefined, side: Side) {
  const filtered = detections?.filter((detection) => detection.side === side)
  return filtered?.length ? filtered : undefined
}

function cleanBorderCoords(coords?: Partial<Record<BorderEdge, number[] | null>>) {
  if (!coords) return undefined
  const result: Partial<Record<BorderEdge, number[]>> = {}
  ;(['left', 'right', 'top', 'bottom'] as BorderEdge[]).forEach((edge) => {
    const values = coords[edge]
    if (values?.length === 4) result[edge] = values
  })
  return hasBorderCoords(result) ? result : undefined
}

function flatCenteringLines(match: MostSimilarGrade, side: Side): CenteringLineSettings {
  if (side === 'front') {
    return {
      leftOuterRatio: match.front_left_outer_ratio,
      rightOuterRatio: match.front_right_outer_ratio,
      topOuterRatio: match.front_top_outer_ratio,
      bottomOuterRatio: match.front_bottom_outer_ratio,
      leftInnerRatio: match.front_left_inner_ratio,
      rightInnerRatio: match.front_right_inner_ratio,
      topInnerRatio: match.front_top_inner_ratio,
      bottomInnerRatio: match.front_bottom_inner_ratio,
    }
  }
  return {
    leftOuterRatio: match.back_left_outer_ratio,
    rightOuterRatio: match.back_right_outer_ratio,
    topOuterRatio: match.back_top_outer_ratio,
    bottomOuterRatio: match.back_bottom_outer_ratio,
    leftInnerRatio: match.back_left_inner_ratio,
    rightInnerRatio: match.back_right_inner_ratio,
    topInnerRatio: match.back_top_inner_ratio,
    bottomInnerRatio: match.back_bottom_inner_ratio,
  }
}

function hasBorderCoords(coords?: Partial<Record<BorderEdge, number[]>>) {
  return Boolean(coords && Object.values(coords).some((value) => value?.length === 4))
}

function hasCenteringLines(lines?: CenteringLineSettings) {
  return Boolean(lines && Object.values(lines).some((value) => value !== undefined))
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function firstNonEmpty(...values: Array<string | null | undefined>) {
  return values.find((value) => typeof value === 'string' && value.trim())?.trim()
}

function getPercentDisplay(value?: number | null) {
  const percent = getPercentNumber(value)
  return percent === null ? null : `${percent.toFixed(1)}%`
}

function getPercentNumber(value?: number | null) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  return value <= 1 ? value * 100 : value
}

function getSimilarityClosenessLabel(value?: number | null) {
  const percent = getPercentNumber(value)
  if (percent === null) return null
  if (percent >= 95) return 'very close'
  if (percent >= 85) return 'close'
  if (percent >= 70) return 'somewhat close'
  return 'different'
}

function getMatchPercentage(summary: ClosestMatchSummary | null | undefined, key: string) {
  const value = summary?.match_percentages?.[key]
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function getDamageMatch(summary: ClosestMatchSummary | null | undefined) {
  const rows = [
    { value: getMatchPercentage(summary, 'count'), weight: 4 },
    { value: getMatchPercentage(summary, 'corner_damage_count'), weight: 2 },
  ].filter((row): row is { value: number; weight: number } => row.value !== null)
  if (rows.length === 0) return null
  const totalWeight = rows.reduce((total, row) => total + row.weight, 0)
  return rows.reduce((total, row) => total + row.value * row.weight, 0) / totalWeight
}

function getClosestMatchBreakdownRows(summary: ClosestMatchSummary | null | undefined) {
  return [
    {
      key: 'centering',
      label: 'Centering',
      value: getMatchPercentage(summary, 'front_centering_error'),
    },
    {
      key: 'damage',
      label: 'Surface & edge wear',
      value: getDamageMatch(summary),
    },
  ]
}
